import type { PayloadAction } from "@reduxjs/toolkit"
import { createSelector, createSlice } from "@reduxjs/toolkit"

import { meetingApi } from "@/api/meetingApi"
import type {
  FavoriteMeeting,
  MeetingProfile,
  RoomHistoryItem,
} from "@/api/types"
import { logger } from "@/lib/logger"
import { logoutAction } from "@/store/session/logoutAction"
import type { AppDispatch, RootState } from "@/store/store"
import { selectActiveContext } from "@/store/workspace/slice"

interface WorkspaceMeetingsState {
  historyByWorkspaceId: Record<string, RoomHistoryItem[]>
  historyLastFetchedAtByWorkspaceId: Record<string, number>
  isFetchingHistoryByWorkspaceId: Record<string, boolean>

  profilesByWorkspaceId: Record<string, MeetingProfile[]>
  profilesLastFetchedAtByWorkspaceId: Record<string, number>
  isFetchingProfilesByWorkspaceId: Record<string, boolean>

  favoritesByWorkspaceId: Record<string, FavoriteMeeting[]>
  favoritesLastFetchedAtByWorkspaceId: Record<string, number>
  isFetchingFavoritesByWorkspaceId: Record<string, boolean>
}

const initialState: WorkspaceMeetingsState = {
  historyByWorkspaceId: {},
  historyLastFetchedAtByWorkspaceId: {},
  isFetchingHistoryByWorkspaceId: {},

  profilesByWorkspaceId: {},
  profilesLastFetchedAtByWorkspaceId: {},
  isFetchingProfilesByWorkspaceId: {},

  favoritesByWorkspaceId: {},
  favoritesLastFetchedAtByWorkspaceId: {},
  isFetchingFavoritesByWorkspaceId: {},
}

const WORKSPACE_MEETINGS_STALE_AFTER_MS = 60 * 1000

const workspaceMeetingsSlice = createSlice({
  name: "workspaceMeetings",
  initialState,
  reducers: {
    setMeetingHistory: (
      state,
      action: PayloadAction<{
        workspaceId: string
        history: RoomHistoryItem[]
      }>,
    ) => {
      state.historyByWorkspaceId[action.payload.workspaceId] =
        action.payload.history
      state.historyLastFetchedAtByWorkspaceId[action.payload.workspaceId] =
        Date.now()
    },
    setIsFetchingHistory: (
      state,
      action: PayloadAction<{ workspaceId: string; value: boolean }>,
    ) => {
      state.isFetchingHistoryByWorkspaceId[action.payload.workspaceId] =
        action.payload.value
    },
    removeMeetingFromWorkspaceHistory: (
      state,
      action: PayloadAction<{ workspaceId: string; roomSessionId: string }>,
    ) => {
      const list = state.historyByWorkspaceId[action.payload.workspaceId]
      if (list) {
        state.historyByWorkspaceId[action.payload.workspaceId] = list.filter(
          (item) => item.roomSessionId !== action.payload.roomSessionId,
        )
      }
    },

    setProfiles: (
      state,
      action: PayloadAction<{
        workspaceId: string
        profiles: MeetingProfile[]
      }>,
    ) => {
      state.profilesByWorkspaceId[action.payload.workspaceId] =
        action.payload.profiles
      state.profilesLastFetchedAtByWorkspaceId[action.payload.workspaceId] =
        Date.now()
    },
    setIsFetchingProfiles: (
      state,
      action: PayloadAction<{ workspaceId: string; value: boolean }>,
    ) => {
      state.isFetchingProfilesByWorkspaceId[action.payload.workspaceId] =
        action.payload.value
    },

    setWorkspaceFavorites: (
      state,
      action: PayloadAction<{
        workspaceId: string
        favorites: FavoriteMeeting[]
      }>,
    ) => {
      state.favoritesByWorkspaceId[action.payload.workspaceId] =
        action.payload.favorites
      state.favoritesLastFetchedAtByWorkspaceId[action.payload.workspaceId] =
        Date.now()
    },
    setIsFetchingFavorites: (
      state,
      action: PayloadAction<{ workspaceId: string; value: boolean }>,
    ) => {
      state.isFetchingFavoritesByWorkspaceId[action.payload.workspaceId] =
        action.payload.value
    },

    addWorkspaceRoomFavorite: (
      state,
      action: PayloadAction<{
        workspaceId: string
        meeting: FavoriteMeeting
      }>,
    ) => {
      const list = state.favoritesByWorkspaceId[action.payload.workspaceId]
      if (list) {
        list.unshift(action.payload.meeting)
      } else {
        state.favoritesByWorkspaceId[action.payload.workspaceId] = [
          action.payload.meeting,
        ]
      }
    },
    removeWorkspaceRoomFavorite: (
      state,
      action: PayloadAction<{ workspaceId: string; roomId: string }>,
    ) => {
      const list = state.favoritesByWorkspaceId[action.payload.workspaceId]
      if (list) {
        state.favoritesByWorkspaceId[action.payload.workspaceId] = list.filter(
          (f) => f.id !== action.payload.roomId,
        )
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logoutAction, () => initialState)
  },
})

export const {
  setMeetingHistory,
  setIsFetchingHistory,
  removeMeetingFromWorkspaceHistory,
  setProfiles,
  setIsFetchingProfiles,
  setWorkspaceFavorites,
  setIsFetchingFavorites,
  addWorkspaceRoomFavorite,
  removeWorkspaceRoomFavorite,
} = workspaceMeetingsSlice.actions

export const selectWorkspaceMeetingHistory =
  (workspaceId: string) => (state: RootState) =>
    state.workspaceMeetings.historyByWorkspaceId[workspaceId] ?? []

export const selectWorkspaceMeetingProfiles =
  (workspaceId: string) => (state: RootState) =>
    state.workspaceMeetings.profilesByWorkspaceId[workspaceId] ?? []

export const selectWorkspaceFavoriteMeetings =
  (workspaceId: string) => (state: RootState) =>
    state.workspaceMeetings.favoritesByWorkspaceId[workspaceId] ?? []

export const selectContextMeetingHistory = createSelector(
  [
    selectActiveContext,
    (state: RootState) => state.workspaceMeetings.historyByWorkspaceId,
    (state: RootState) => state.history.meetingHistory,
  ],
  (activeContext, workspaceHistory, personalHistory) => {
    if (activeContext.type === "personal") return personalHistory
    return workspaceHistory[activeContext.workspaceId] ?? []
  },
)

export const selectContextFavoriteMeetings = createSelector(
  [
    selectActiveContext,
    (state: RootState) => state.workspaceMeetings.favoritesByWorkspaceId,
    (state: RootState) => state.favorites.meetingFavoriteDetails,
  ],
  (activeContext, workspaceFavorites, personalFavorites) => {
    if (activeContext.type === "personal")
      return Object.values(personalFavorites)
    return workspaceFavorites[activeContext.workspaceId] ?? []
  },
)

export const selectContextIsRoomFavorite = (roomId: string) =>
  createSelector(
    [
      selectActiveContext,
      (state: RootState) => state.workspaceMeetings.favoritesByWorkspaceId,
      (state: RootState) => state.favorites.meetingFavorites,
    ],
    (activeContext, workspaceFavorites, personalFavIds) => {
      if (activeContext.type === "personal")
        return personalFavIds.includes(roomId)
      const wsFavs = workspaceFavorites[activeContext.workspaceId] ?? []
      return wsFavs.some((f) => f.id === roomId)
    },
  )

const isStale = (
  lastFetchedAt: number | null | undefined,
  ttlMs: number,
): boolean => {
  if (lastFetchedAt == null) return true
  return Date.now() - lastFetchedAt > ttlMs
}

interface FetchOptions {
  force?: boolean
}

const historyRequests: Record<string, Promise<RoomHistoryItem[]> | null> = {}
const profilesRequests: Record<string, Promise<MeetingProfile[]> | null> = {}
const favoritesRequests: Record<string, Promise<FavoriteMeeting[]> | null> = {}

const WORKSPACE_HISTORY_PAGE_SIZE = 30
const WORKSPACE_HISTORY_MAX_COUNT = 50

export const ensureWorkspaceMeetingHistory =
  (workspaceId: string, options: FetchOptions & { count?: number } = {}) =>
  async (
    dispatch: AppDispatch,
    getState: () => RootState,
  ): Promise<RoomHistoryItem[]> => {
    const state = getState().workspaceMeetings
    const cached = state.historyByWorkspaceId[workspaceId]
    const lastFetched = state.historyLastFetchedAtByWorkspaceId[workspaceId]
    const count = options.count ?? WORKSPACE_HISTORY_PAGE_SIZE

    if (
      !options.force &&
      !isStale(lastFetched, WORKSPACE_MEETINGS_STALE_AFTER_MS) &&
      cached?.length
    ) {
      return cached
    }

    if (historyRequests[workspaceId]) {
      return historyRequests[workspaceId]!
    }

    dispatch(setIsFetchingHistory({ workspaceId, value: true }))

    historyRequests[workspaceId] = (async () => {
      try {
        const history = await meetingApi.getWorkspaceMeetingHistory(
          workspaceId,
          { count },
        )
        dispatch(setMeetingHistory({ workspaceId, history }))
        return history
      } catch (error) {
        logger.error(
          "[workspaceMeetingsSlice] Failed to fetch meeting history",
          error,
        )
        throw error
      } finally {
        dispatch(setIsFetchingHistory({ workspaceId, value: false }))
        historyRequests[workspaceId] = null
      }
    })()

    return historyRequests[workspaceId]!
  }

export const loadMoreWorkspaceMeetingHistory =
  (workspaceId: string) =>
  async (
    dispatch: AppDispatch,
    getState: () => RootState,
  ): Promise<RoomHistoryItem[]> => {
    const current =
      getState().workspaceMeetings.historyByWorkspaceId[workspaceId] ?? []
    if (current.length < WORKSPACE_HISTORY_PAGE_SIZE) return current
    if (current.length >= WORKSPACE_HISTORY_MAX_COUNT) return current
    return dispatch(
      ensureWorkspaceMeetingHistory(workspaceId, {
        force: true,
        count: WORKSPACE_HISTORY_MAX_COUNT,
      }),
    )
  }

export { WORKSPACE_HISTORY_MAX_COUNT, WORKSPACE_HISTORY_PAGE_SIZE }

export const ensureWorkspaceMeetingProfiles =
  (workspaceId: string, options: FetchOptions = {}) =>
  async (
    dispatch: AppDispatch,
    getState: () => RootState,
  ): Promise<MeetingProfile[]> => {
    const state = getState().workspaceMeetings
    const cached = state.profilesByWorkspaceId[workspaceId]
    const lastFetched = state.profilesLastFetchedAtByWorkspaceId[workspaceId]

    if (
      !options.force &&
      !isStale(lastFetched, WORKSPACE_MEETINGS_STALE_AFTER_MS) &&
      cached?.length
    ) {
      return cached
    }

    if (profilesRequests[workspaceId]) {
      return profilesRequests[workspaceId]!
    }

    dispatch(setIsFetchingProfiles({ workspaceId, value: true }))

    profilesRequests[workspaceId] = (async () => {
      try {
        const profiles =
          await meetingApi.getWorkspaceMeetingProfiles(workspaceId)
        dispatch(setProfiles({ workspaceId, profiles }))
        return profiles
      } catch (error) {
        logger.error(
          "[workspaceMeetingsSlice] Failed to fetch meeting profiles",
          error,
        )
        throw error
      } finally {
        dispatch(setIsFetchingProfiles({ workspaceId, value: false }))
        profilesRequests[workspaceId] = null
      }
    })()

    return profilesRequests[workspaceId]!
  }

export const ensureWorkspaceFavoriteMeetings =
  (workspaceId: string, options: FetchOptions = {}) =>
  async (
    dispatch: AppDispatch,
    getState: () => RootState,
  ): Promise<FavoriteMeeting[]> => {
    const state = getState().workspaceMeetings
    const cached = state.favoritesByWorkspaceId[workspaceId]
    const lastFetched = state.favoritesLastFetchedAtByWorkspaceId[workspaceId]

    if (
      !options.force &&
      !isStale(lastFetched, WORKSPACE_MEETINGS_STALE_AFTER_MS) &&
      cached?.length
    ) {
      return cached
    }

    if (favoritesRequests[workspaceId]) {
      return favoritesRequests[workspaceId]!
    }

    dispatch(setIsFetchingFavorites({ workspaceId, value: true }))

    favoritesRequests[workspaceId] = (async () => {
      try {
        const favorites =
          await meetingApi.getWorkspaceFavoriteMeetings(workspaceId)
        dispatch(setWorkspaceFavorites({ workspaceId, favorites }))
        return favorites
      } catch (error) {
        logger.error(
          "[workspaceMeetingsSlice] Failed to fetch favorite meetings",
          error,
        )
        throw error
      } finally {
        dispatch(setIsFetchingFavorites({ workspaceId, value: false }))
        favoritesRequests[workspaceId] = null
      }
    })()

    return favoritesRequests[workspaceId]!
  }

export default workspaceMeetingsSlice.reducer
